import { useMemo, useState } from 'react';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, FieldLabel, FieldRoot, Select, SelectContent, SelectItem, TextField } from '@zep-us/zus';
import { ChevronDownIcon, MagnifingglassIcon } from '@zep-us/zus-icons';
import { useGetCategoryTreesByCountry } from '@/api';
import { openSchoolSearchModal, useGetSubjectsByCountry } from '@/module/auth/sign-up';
import { isAllGrades } from '@/views/build/util';
export interface ForcedSchoolGradeFormValues {
  schoolName: string;
  schoolTypeCode: string;
  schoolGradeCode: string;
}
export interface ForcedSchoolGradeModalProps {
  /**
   * 저장 요청을 수행한다. 성공 시 상위(overlay)에서 모달을 닫고,
   * 실패 시 reject 하여 모달이 제출 상태를 되돌리도록 한다.
   */
  onSubmit: (values: ForcedSchoolGradeFormValues) => Promise<void>;
  /** 학교 찾기에 넘길 국가 코드. 없으면 'KR'로 가정한다. */
  countryCode?: string;
}

/** 학년 없이 운영되는 학교 유형 (대학교/기타/유치원). SignInput.tsx의 판정과 동일. */
const NO_GRADE_SCHOOL_TYPES = new Set(['COLLEGE', 'OTHER', 'KINDERGARTEN']);
interface SelectedSchool {
  schoolName: string;
  schoolType: string;
  /** 학교 찾기에서 반환된 학교 유형 코드. 저장 시 함께 전송해 자료실 학교급 필터가 매칭되도록 한다. */
  schoolTypeCode: string;
  /** 학교 찾기에서 반환된 학년 코드. 학년 없음 유형에서는 이 값을 그대로 제출한다. */
  schoolGradeCode: string;
  /** 학교 찾기 당시의 학년(숫자 문자열). 현재 선택 학년과 같을 때만 학년코드 폴백을 허용하는 데 쓴다. */
  schoolGrade: string;
}
const getGradeSelectLabel = (grade: string | null): string => {
  if (!grade) return '';
  if (isAllGrades(grade)) return '전학년';
  return `${grade}학년`;
};

/**
 * 로그인 직후 닫기 불가능한 학교·학년 필수 입력 모달(프레젠테이셔널).
 * open 을 true 로 고정하고 ESC/외부클릭 콜백을 모두 preventDefault 하므로,
 * 닫히는 경로는 onSubmit 성공(훅이 unmount) 뿐이다. 학교 찾기는 회원가입의
 * openSchoolSearchModal 을 재사용한다.
 */
export const ForcedSchoolGradeModal = ({
  onSubmit,
  countryCode
}: ForcedSchoolGradeModalProps) => {
  const resolvedCountryCode = countryCode || 'KR';
  const [selectedSchool, setSelectedSchool] = useState<SelectedSchool | null>(null);
  // 사용자가 선택 중인 학년(숫자 문자열). 학교 찾기 결과의 학년을 기본값으로 사용한다.
  const [selectedGrade, setSelectedGrade] = useState<string | null>(null);
  const [isGradeSelectOpen, setIsGradeSelectOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 학교 찾기 모달이 열린 동안에는 이 강제 모달의 focus trap/inert 를 풀어
  // (modal=false) 중첩된 검색 모달의 입력이 키 입력을 받을 수 있게 한다.
  // 검색 모달 자체 dim 이 배경을 덮으므로 접근 차단은 유지된다.
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const isNoGradeSchool = selectedSchool ? NO_GRADE_SCHOOL_TYPES.has(selectedSchool.schoolType) : false;

  // 선택된 학교 유형 기준 학년 목록 (useSignUpSchoolGrade 와 동일한 로직, RHF 미사용).
  const {
    data: categoryTreeData
  } = useGetCategoryTreesByCountry(resolvedCountryCode, {
    query: {
      enabled: !!selectedSchool && !isNoGradeSchool
    }
  });
  const grades = useMemo(() => {
    if (!selectedSchool || isNoGradeSchool) return [];
    const schoolInfoTree = categoryTreeData?.data?.schoolInfoTree;
    if (!schoolInfoTree) return [];
    const schoolNode = schoolInfoTree.children.find(node => node.name === selectedSchool.schoolType);
    if (!schoolNode || !schoolNode.children) return [];
    return schoolNode.children.filter(node => node.type === 'GRADE').map(gradeNode => parseInt(gradeNode.categoryGroupId, 10));
  }, [categoryTreeData, selectedSchool, isNoGradeSchool]);
  const gradeOptions = useMemo(() => {
    if (grades.includes(0)) return [];
    // 99 등 전학년 코드는 "전학년"으로 표기(회원가입 플로우의 isAllGrades 판정과 동일).
    return grades.map(gradeNumber => ({
      value: String(gradeNumber),
      label: getGradeSelectLabel(String(gradeNumber))
    }));
  }, [grades]);

  // 학년 변경 시 schoolGradeCode 재계산 (useSetSchoolTypeAndGradeCode 와 동일한 로직).
  const canDeriveGradeCode = !!selectedSchool && !isNoGradeSchool && !!selectedGrade && !isNaN(Number(selectedGrade));
  const {
    data: subjects,
    isFetching: isSubjectsFetching
  } = useGetSubjectsByCountry({
    countryCode: resolvedCountryCode,
    schoolType: selectedSchool?.schoolType ?? '',
    grade: canDeriveGradeCode ? Number(selectedGrade) : 0
  }, {
    query: {
      enabled: canDeriveGradeCode
    }
  });

  // 과목 API 빈 응답 시 검색 학년코드로 폴백하되, 검색 당시 학년에서 바꾸지 않은 경우로만
  // 한정한다. 학년을 바꿨는데 이전 학년코드로 저장되면 UI(바뀐 학년)와 저장값이 어긋난다.
  const canUseSearchGradeCodeFallback = !!selectedSchool && selectedGrade === selectedSchool.schoolGrade;
  const resolvedSchoolGradeCode = !selectedSchool ? null : isNoGradeSchool ?
  // 무학년 학교를 검색에서 고르면 schoolGradeCode 가 빌 수 있다. 닫을 수 없는 모달에서
  // 갇히지 않도록 직접 입력 경로와 동일한 sentinel 로 보정한다.
  selectedSchool.schoolGradeCode || 'noGradeCode' : subjects?.[0]?.categoryTreeId ? subjects[0].categoryTreeId.slice(0, 5) : canUseSearchGradeCodeFallback ? selectedSchool.schoolGradeCode || null : null;

  // schoolTypeCode 는 검색 결과값이 비동기 계산 전이라 빌 수 있다. 과목 API 값을 우선 쓰고
  // 없으면 검색값으로 폴백해 자료실 학교급 필터 정확도를 높인다. 단 스키마상 선택값이라
  // (schoolTypeCode: z.string(), min 없음) 제출을 막지는 않는다 — 무학년 학교처럼 유도가
  // 안 되는 경우까지 닫을 수 없는 모달에서 가두지 않기 위함.
  const resolvedSchoolTypeCode = subjects?.[0]?.categoryTreeId?.slice(0, 3) || selectedSchool?.schoolTypeCode || '';
  const isValid = !!selectedSchool?.schoolName.trim() && !!resolvedSchoolGradeCode && !(canDeriveGradeCode && isSubjectsFetching);
  const handleOpenSchoolSearch = () => {
    setIsSearchOpen(true);
    openSchoolSearchModal({
      countryCode: resolvedCountryCode,
      // 이 모달은 교사 전용(useForcedSchoolGradeModal 의 shouldShowModal 게이트)이라
      // 학교 검색 안의 학년 셀렉에도 전학년을 노출한다.
      userType: 'teacher',
      onConfirm: data => {
        setSelectedSchool({
          schoolName: data.schoolName,
          schoolType: data.schoolType,
          schoolTypeCode: data.schoolTypeCode,
          schoolGradeCode: data.schoolGradeCode,
          schoolGrade: data.schoolGrade || ''
        });
        setSelectedGrade(data.schoolGrade || null);
      },
      onClose: () => setIsSearchOpen(false)
    });
  };
  const handleSubmit = async () => {
    if (!isValid || !selectedSchool || !resolvedSchoolGradeCode || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit({
        schoolName: selectedSchool.schoolName.trim(),
        schoolTypeCode: resolvedSchoolTypeCode,
        schoolGradeCode: resolvedSchoolGradeCode
      });
      // 성공 시 상위 overlay 에서 close() 되어 언마운트된다.
    } catch {
      setIsSubmitting(false);
    }
  };
  return <Dialog open modal={!isSearchOpen} data-sentry-element="Dialog" data-sentry-component="ForcedSchoolGradeModal" data-sentry-source-file="ForcedSchoolGradeModal.tsx">
      <DialogContent onEscapeKeyDown={event => event.preventDefault()} onPointerDownOutside={event => event.preventDefault()} onInteractOutside={event => event.preventDefault()} hasDim={!isSearchOpen} className={`w-[calc(100vw-32px)] max-w-[520px] rounded-lg p-0${isSearchOpen ? ' hidden' : ''}`} data-sentry-element="DialogContent" data-sentry-source-file="ForcedSchoolGradeModal.tsx">
        <DialogHeader className="px-md pb-xs pt-lg mobile-lg:px-lg" data-sentry-element="DialogHeader" data-sentry-source-file="ForcedSchoolGradeModal.tsx">
          <DialogTitle className="text-heading-md-bold text-default" data-sentry-element="DialogTitle" data-sentry-source-file="ForcedSchoolGradeModal.tsx">
            학교 정보를 입력해 주세요
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-md px-md mobile-lg:px-lg">
          <DialogDescription className="text-body-md-regular text-default" data-sentry-element="DialogDescription" data-sentry-source-file="ForcedSchoolGradeModal.tsx">
            학년에 맞는 퀴즈를 추천해 드리기 위해 학교·학년 정보가 필요해요.
          </DialogDescription>
          <div className="flex flex-col gap-md">
            <TextField.Root required data-sentry-element="unknown" data-sentry-source-file="ForcedSchoolGradeModal.tsx">
              <TextField.Label rightSlot={<span className="text-caption-lg-semibold text-destructive">
                    *
                  </span>} data-sentry-element="unknown" data-sentry-source-file="ForcedSchoolGradeModal.tsx">
                학교
              </TextField.Label>
              <div className="flex gap-xs">
                <TextField.Input readOnly placeholder="학교명으로 검색해 주세요" value={selectedSchool?.schoolName ?? ''} onClick={handleOpenSchoolSearch} className="cursor-pointer" data-sentry-element="unknown" data-sentry-source-file="ForcedSchoolGradeModal.tsx" />
                <Button variant="outlined" intent="alternative" size="lg" className="shrink-0" leftSlot={<MagnifingglassIcon size="sm" />} onClick={handleOpenSchoolSearch} data-sentry-element="Button" data-sentry-source-file="ForcedSchoolGradeModal.tsx">
                  학교 찾기
                </Button>
              </div>
            </TextField.Root>
            {selectedSchool && isNoGradeSchool && <FieldRoot required>
                <FieldLabel rightSlot={<span className="text-caption-lg-semibold text-destructive">
                      *
                    </span>}>
                  학년
                </FieldLabel>
                <TextField.Input readOnly value="학년 없음" />
              </FieldRoot>}
            {selectedSchool && !isNoGradeSchool && <FieldRoot required>
                <FieldLabel rightSlot={<span className="text-caption-lg-semibold text-destructive">
                      *
                    </span>}>
                  학년
                </FieldLabel>
                <Select isOpen={isGradeSelectOpen} open={() => setIsGradeSelectOpen(true)} close={() => setIsGradeSelectOpen(false)} options={gradeOptions} placeholder="학년을 선택해 주세요" renderedValue={getGradeSelectLabel(selectedGrade)} icon={<ChevronDownIcon size="sm" />}>
                  <SelectContent onPointerDownOutside={() => setIsGradeSelectOpen(false)}>
                    {gradeOptions.map(option => <SelectItem key={option.value} checked={selectedGrade === option.value} value={option.value} label={option.label} onClick={() => {
                  setSelectedGrade(option.value);
                  setIsGradeSelectOpen(false);
                }}>
                        {option.label}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRoot>}
          </div>
        </div>
        <DialogFooter className="p-md mobile-lg:p-lg" data-sentry-element="DialogFooter" data-sentry-source-file="ForcedSchoolGradeModal.tsx">
          <Button variant="contained" intent="strong" size="lg" className="w-full" disabled={!isValid || isSubmitting} onClick={handleSubmit} data-sentry-element="Button" data-sentry-source-file="ForcedSchoolGradeModal.tsx">
            입력 완료
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
};